import type { PmNodeConverter, PmNodeJson } from './../pm-converters/types';
import { TIPTAP_TABLE_HEADER_CELL_TYPE } from './../ricos-content';
import type { Node, TableCellData, TableData } from './../ricos-schema/legacy/wix/rich_content/v1';
import { Node_Type } from './../ricos-schema/legacy/wix/rich_content/v1';
import type { TableCellNode, TableNode, TableRowNode } from './../ricos-schema/next-schema';

export const tableConverter: PmNodeConverter = {
  toPm: {
    types: [Node_Type.TABLE],
    // @ts-ignore next node type
    convert: (node: TableNode, visit: (node: TableRowNode | TableCellNode) => PmNodeJson[]) => ({
      type: Node_Type.TABLE,
      attrs: {
        id: node.id,
        ...node.tableData,
      },
      content: node.nodes.map((rowNode, rowIndex) => ({
        type: Node_Type.TABLE_ROW,
        attrs: { id: rowNode.id, height: node.tableData?.dimensions?.rowsHeight?.[rowIndex] },
        content: rowNode.nodes.map((colNode, colIndex) => {
          const { colsWidthRatio, colsMinWidth } = node.tableData?.dimensions || {};
          const widthInPixel = ratioWidthToPixel(colsWidthRatio, colsMinWidth);
          return {
            type:
              (rowIndex === 0 && node.tableData?.rowHeader) ||
              (colIndex === 0 && node.tableData?.columnHeader)
                ? TIPTAP_TABLE_HEADER_CELL_TYPE
                : Node_Type.TABLE_CELL,
            attrs: {
              ...colNode.tableCellData,
              id: colNode.id,
              colwidth: widthInPixel?.[colIndex] && [widthInPixel[colIndex]],
            },
            content: visit(colNode),
          };
        }),
      })),
    }),
  },
  fromPm: {
    types: [Node_Type.TABLE],
    // @ts-ignore next node type
    convert: (node: PmNodeJson, visit: (node: PmNodeJson) => Node[]) => {
      const { attrs = {} } = node;
      const { id, dimensions, ...data } = attrs;
      return {
        type: Node_Type.TABLE,
        id,
        tableData: {
          ...(data as TableData),
          dimensions: {
            ...dimensions,
            rowsHeight: node.content?.map(node => node.attrs?.height),
          },
        },
        nodes: visit(node),
      };
    },
  },
};

export const tableRowConverter: PmNodeConverter = {
  toPm: {
    types: [Node_Type.TABLE_ROW],
    // @ts-ignore next node type
    convert: (node: TableRowNode, visit: (node: TableRowNode) => PmNodeJson[]) => ({
      type: Node_Type.TABLE_ROW,
      attrs: {
        id: node.id,
      },
      content: visit(node),
    }),
  },
  fromPm: {
    types: [Node_Type.TABLE_ROW],
    // @ts-ignore next node type
    convert: (node: PmNodeJson, visit: (node: PmNodeJson) => Node[]) => {
      const { attrs = {} } = node;
      const { id } = attrs;
      return {
        type: Node_Type.TABLE_ROW,
        id,
        nodes: visit(node),
      };
    },
  },
};

export const tableCellConverter: PmNodeConverter = {
  toPm: {
    types: [Node_Type.TABLE_CELL],
    // @ts-ignore next node type
    convert: (node: TableCellNode, visit: (node: TableCellNode) => PmNodeJson[]) => ({
      type: Node_Type.TABLE_CELL,
      attrs: {
        id: node.id,
        ...node.tableCellData,
      },
      content: visit(node),
    }),
  },
  fromPm: {
    types: [TIPTAP_TABLE_HEADER_CELL_TYPE, Node_Type.TABLE_CELL],
    // @ts-ignore next node type
    convert: (node: PmNodeJson, visit: (node: PmNodeJson) => Node[]) => {
      const { attrs = {} } = node;
      const { id, colwidth: _, ...data } = attrs;
      return {
        type: Node_Type.TABLE_CELL,
        id,
        tableCellData: {
          ...(data as TableCellData),
        },
        nodes: visit(node),
      };
    },
  },
};

const ratioWidthToPixel = (colsWidthRatio: $TSFixMe, colsMinWidth: $TSFixMe) => {
  if (colsWidthRatio && colsMinWidth) {
    const smallestColRatio = Math.min(...colsWidthRatio);
    const smallestMinWidth = Math.min(...colsMinWidth);
    const ratio = smallestMinWidth / smallestColRatio;
    return colsWidthRatio.map((width: $TSFixMe) => width * ratio);
  }
};
